import EnvironmentService from "@/env/EnvironmentService";
import { IAgreementItemViewModel, AgreementType, isAgreementChecked, Agreement, AgreementMethod, AgreementOrganisation } from "./Agreements";
import { ProductType } from "@/commons/enums/ProductType";
import { OrganisationId } from "@/models/enums/OrganisationId";
import AuthService from "@/modules/user/AuthService";

const getDomainAgreementByKey = (key: AgreementType) => {
  return EnvironmentService?.Environment?.getDomainAgreement(key)
};

export const organisationName = EnvironmentService.Environment.agreementOrganisation;

export const organisationNameWithPartner = (): Extract<AgreementOrganisation, 'Imobiliare' | 'Lendi' | 'Finamax'> =>
  organisationName !== 'Lendi' 
    ? 'Imobiliare'
    : AuthService.User?.isUserOf.FINAMAX
      ? 'Finamax'
      : 'Lendi';

export const getAgreementViews = (): IAgreementItemViewModel[] => {
  const views: IAgreementItemViewModel[] = [
    {
      title: getDomainAgreementByKey(`Accepts${organisationName}PersonalDataProcessing`)?.title,
      key: `Accepts${organisationName}PersonalDataProcessing`,
      label: getDomainAgreementByKey(`Accepts${organisationName}PersonalDataProcessing`)?.label,
      isEditable: false,
      required: (v) => !!v && (!isAgreementChecked(v, 'AcceptsFinamaxPersonalDataProcessing') || AuthService.User?.organisationId === OrganisationId.LENDI),
      productTypes: [ProductType.SME, ProductType.CASH, ProductType.MORTGAGE,],
    },
    {
      title: getDomainAgreementByKey(`Accepts${organisationName}CommercialInformation`)?.title,
      key: `Accepts${organisationName}CommercialInformation`,
      label: getDomainAgreementByKey(`Accepts${organisationName}CommercialInformation`)?.label,
      isEditable: false,
      required: (v) => true,
      productTypes: [ProductType.SME, ProductType.CASH, ProductType.MORTGAGE,],
    },
    {
      title: getDomainAgreementByKey(`Accepts${organisationName}Marketing`)?.title,
      key: `Accepts${organisationName}Marketing`,
      label: getDomainAgreementByKey(`Accepts${organisationName}Marketing`)?.label,
      required: (v) => false,
      isEditable: true,
      productTypes: [ProductType.SME, ProductType.CASH, ProductType.MORTGAGE],
    },
    {
      title: getDomainAgreementByKey('AcceptsBigPersonalDataProcessing')?.title,
      key: 'AcceptsBigPersonalDataProcessing',
      label: getDomainAgreementByKey('AcceptsBigPersonalDataProcessing')?.label,
      isEditable: true,
      required: (v) => false,
      productTypes: [ProductType.SME, ProductType.CASH, ProductType.MORTGAGE, ProductType.REAL_ESTATE,],
    },
    {
      title: getDomainAgreementByKey('AcceptsHomeEstatePersonalDataProcessing')?.title,
      key: 'AcceptsHomeEstatePersonalDataProcessing',
      label: getDomainAgreementByKey('AcceptsHomeEstatePersonalDataProcessing')?.label,
      isEditable: false,
      isRealEstate: true,
      required: (v) => true,
      productTypes: [ProductType.REAL_ESTATE,],
    },
    {
      title: getDomainAgreementByKey('AcceptsHomeEstateMarketing')?.title,
      key: 'AcceptsHomeEstateMarketing',
      label: getDomainAgreementByKey('AcceptsHomeEstateMarketing')?.label,
      isEditable: false,
      isRealEstate: true,
      required: (v) => false,
      productTypes: [ProductType.REAL_ESTATE,],
    },
  ];

  if (AuthService.User?.isUserOf.FINAMAX) {
    const finamaxViews: IAgreementItemViewModel[] = [
      {
        title: getDomainAgreementByKey('AcceptsFinamaxPersonalDataProcessing')?.title,
        key: 'AcceptsFinamaxPersonalDataProcessing',
        label: getDomainAgreementByKey('AcceptsFinamaxPersonalDataProcessing')?.label,
        organisationIds: [OrganisationId.FINAMAX,],
        isEditable: false,
        required: (v) => !!v && !isAgreementChecked(v, 'AcceptsFinamaxPersonalDataProcessing'),
        productTypes: [ProductType.SME, ProductType.CASH, ProductType.MORTGAGE,],
      },
      {
        title: getDomainAgreementByKey('AcceptsFinamaxCommercialInformation')?.title,
        key: 'AcceptsFinamaxCommercialInformation',
        label: getDomainAgreementByKey('AcceptsFinamaxCommercialInformation')?.label,
        organisationIds: [OrganisationId.FINAMAX,],
        isEditable: false,
        required: (v) => true,
        productTypes: [ProductType.SME, ProductType.CASH, ProductType.MORTGAGE, ProductType.REAL_ESTATE,],
      },
      {
        title: getDomainAgreementByKey('AcceptsFinamaxMarketing')?.title,
        key: 'AcceptsFinamaxMarketing',
        label: getDomainAgreementByKey('AcceptsFinamaxMarketing')?.label,
        required: (v) => false,
        isEditable: true,
        organisationIds: [OrganisationId.FINAMAX,],
        productTypes: [ProductType.SME, ProductType.CASH, ProductType.MORTGAGE, ProductType.REAL_ESTATE,],
      },
    ];

    views.push(...finamaxViews);
  }

  return views;
};

export class OldAgreements {
  acceptsProcessingMethod?: AgreementMethod;
  acceptsReceivingInfoMethod?: AgreementMethod;
  acceptsReceivingOffersMethod?: AgreementMethod;
  acceptsBigInquiryMethod?: AgreementMethod;
  acceptsReceivingOffersPartnerMethod?: AgreementMethod;
  acceptsReceivingInfoPartnerMethod?: AgreementMethod;
  acceptsProcessingPartnerMethod?: AgreementMethod;
  acceptsProcessingLeadMethod?: AgreementMethod;
  acceptsRealEstateMethod?: AgreementMethod;
  acceptsBankSecrecyAt?: string;
  acceptsProcessingLeadAt?: string;
  acceptsProcessingAt?: string;
  acceptsReceivingInfoAt?: string;
  acceptsReceivingOffersAt?: string;
  acceptsBigInquiryAt?: string;
  acceptsReceivingOffersPartnerAt?: string;
  acceptsReceivingInfoPartnerAt?: string;
  acceptsProcessingPartnerAt?: string;
  acceptsRealEstateAt?: string;
  acceptsLendiCareCommercialInformation?: boolean;
  acceptsLendiCareCommercialInformationAt?: string;
  acceptsLendiCareCommercialInformationMethod?: AgreementMethod;
  acceptsLendiCarePersonalDataProcessing?: boolean;
  acceptsLendiCarePersonalDataProcessingAt?: string;
  acceptsLendiCarePersonalDataProcessingMethod?: AgreementMethod;
  acceptsLendiCareMarketing?: boolean;
  acceptsLendiCareMarketingAt?: string;
  acceptsLendiCareMarketingMethod?: AgreementMethod;
  acceptsProcessing?: boolean;
  acceptsReceivingInfo?: boolean;
  acceptsReceivingOffers?: boolean;
  acceptsBigInquiry?: boolean;
  acceptsReceivingOffersPartner?: boolean;
  acceptsProcessingPartner?: boolean;
  acceptsReceivingInfoPartner?: boolean;
  acceptsProcessingLead?: boolean;
  acceptsRealEstate?: boolean;
  acceptsBankSecrecy?: boolean;
}

export const mapToOldModelAgreement = (agreements: Agreement[]): Partial<OldAgreements> => {
  const oldModelAgreements: Partial<OldAgreements> = {};
  agreements.forEach(agreement => {
    switch (agreement.type) {
      case 'AcceptsLendiPersonalDataProcessing':
        oldModelAgreements.acceptsProcessing = agreement.value;
        oldModelAgreements.acceptsProcessingAt = agreement.at;
        oldModelAgreements.acceptsProcessingMethod = agreement.method;
        break;
      case 'AcceptsLendiCommercialInformation':
        oldModelAgreements.acceptsReceivingInfo = agreement.value;
        oldModelAgreements.acceptsReceivingInfoAt = agreement.at;
        oldModelAgreements.acceptsReceivingInfoMethod = agreement.method;
        break;
      case 'AcceptsLendiMarketing':
        oldModelAgreements.acceptsReceivingOffers = agreement.value;
        oldModelAgreements.acceptsReceivingOffersAt = agreement.at;
        oldModelAgreements.acceptsReceivingOffersMethod = agreement.method;
        break;
      case 'AcceptsLendiCarePersonalDataProcessing':
        oldModelAgreements.acceptsLendiCarePersonalDataProcessing = agreement.value;
        oldModelAgreements.acceptsLendiCarePersonalDataProcessingAt = agreement.at;
        oldModelAgreements.acceptsLendiCarePersonalDataProcessingMethod = agreement.method;
        break;
      case 'AcceptsLendiCareCommercialInformation':
        oldModelAgreements.acceptsLendiCareCommercialInformation = agreement.value;
        oldModelAgreements.acceptsLendiCareCommercialInformationAt = agreement.at;
        oldModelAgreements.acceptsLendiCareCommercialInformationMethod = agreement.method;
        break;
      case 'AcceptsLendiCareMarketing':
        oldModelAgreements.acceptsLendiCareMarketing = agreement.value;
        oldModelAgreements.acceptsLendiCareMarketingAt = agreement.at;
        oldModelAgreements.acceptsLendiCareMarketingMethod = agreement.method;
        break;
      case 'AcceptsHomeEstatePersonalDataProcessing':
        oldModelAgreements.acceptsRealEstate = agreement.value;
        oldModelAgreements.acceptsRealEstateAt = agreement.at;
        oldModelAgreements.acceptsRealEstateMethod = agreement.method;
        break
    }
  })

  return oldModelAgreements;
}

export const mapFromOldModelAgreement = (agreement: Partial<OldAgreements>, defaultMethod: AgreementMethod = AgreementMethod.SMS): Agreement[] => {
  const agreements: Agreement[] = [];
  Object.keys(agreement).forEach(key => {
    switch (key) {
      case 'acceptsProcessing':
        agreements.push({
          type: 'AcceptsLendiPersonalDataProcessing',
          value: agreement.acceptsProcessing,
          at: agreement.acceptsProcessingAt,
          method: agreement.acceptsProcessingMethod || defaultMethod,
        });
        break;
      case 'acceptsReceivingInfo':
        agreements.push({
          type: 'AcceptsLendiCommercialInformation',
          value: agreement.acceptsReceivingInfo,
          at: agreement.acceptsReceivingInfoAt,
          method: agreement.acceptsReceivingInfoMethod || defaultMethod,
        });
        break;
      case 'acceptsReceivingOffers':
        agreements.push({
          type: 'AcceptsLendiMarketing',
          value: agreement.acceptsReceivingOffers,
          at: agreement.acceptsReceivingOffersAt,
          method: agreement.acceptsReceivingOffersMethod || defaultMethod,
        });
        break;
      case 'acceptsLendiCarePersonalDataProcessing':
        agreements.push({
          type: 'AcceptsLendiCarePersonalDataProcessing',
          value: agreement.acceptsLendiCarePersonalDataProcessing,
          at: agreement.acceptsLendiCarePersonalDataProcessingAt,
          method: agreement.acceptsLendiCarePersonalDataProcessingMethod || defaultMethod,
        });
        break;
      case 'acceptsLendiCareCommercialInformation':
        agreements.push({
          type: 'AcceptsLendiCareCommercialInformation',
          value: agreement.acceptsLendiCareCommercialInformation,
          at: agreement.acceptsLendiCareCommercialInformationAt,
          method: agreement.acceptsLendiCareCommercialInformationMethod || defaultMethod,
        });
        break;
      case 'acceptsLendiCareMarketing':
        agreements.push({
          type: 'AcceptsLendiCareMarketing',
          value: agreement.acceptsLendiCareMarketing,
          at: agreement.acceptsLendiCareMarketingAt,
          method: agreement.acceptsLendiCareMarketingMethod || defaultMethod,
        });
        break;
      case 'acceptsRealEstate':
        agreements.push({
          type: 'AcceptsHomeEstatePersonalDataProcessing',
          value: agreement.acceptsRealEstate,
          at: agreement.acceptsRealEstateAt,
          method: agreement.acceptsRealEstateMethod || defaultMethod,
        });
        break;
    }
  })

  return agreements;
}